export default {
  caseStudy: {
    viewFullCase: '查看完整可视化图表与解析',
    readOnlyBadge: '真实案例 · 只读示例',
    backToLanding: '返回首页',
    startOwnAnalysis: '分析我的照片',
    uploadMyPhoto: '用我的照片试试',
    pageTitle: '建筑 - PhotoScope 真实案例解析',
    pageDescription: '了解 PhotoScope 如何拆解影调、色彩与氛围，弥合上传照片与参考建筑风格之间的差距。',
    notFoundTitle: '未找到案例',
    notFoundDescription: '您访问的案例不存在或已不再提供。',
    loadFailedTitle: '案例加载失败',
    loadFailedDescription: '加载案例时出现问题，请重试。',
    retry: '重试',
  },
  uploadShowcase: {
    title: '查看演示效果',
  },
  heroB: {
    comparison: {
      rawAlt: '对比图 1',
      referenceAlt: '对比图 2',
    },
    gaps: {
      gap_1: {
        title: '曝光定位与中间调提亮',
        brief: '原图整体偏暗，中间调区域保留的细节信息更少。',
        reason: '这一差异决定了整张画面的影调走向，只有先解决它，后续更细致的色彩处理才会显得可信。',
      },
      gap_2: {
        title: '黑场过重与阴影压缩',
        brief: '原图在接近纯黑的区域堆积了过多信息，使光线显得更硬、更闭塞。',
        reason: '沉重的黑场压制，是原图目前还无法呈现参考图那种氛围感的主要原因之一。',
      },
      gap_3: {
        title: '白平衡走向与氛围偏色',
        brief: '原图偏向品红—蓝，而参考图建立在更暖的黄绿氛围基底之上。',
        reason: '这是色板主轴层面的根本差异，它改变的是整张画面的情绪，而非某个单独的物体或局部区域。',
      },
      gap_4: {
        title: '色板分离与天空／墙面质感',
        brief: '原图保留了鲜明的蓝色与陶土色对撞，而参考图则是更柔和的石材色与青色搭配。',
        reason: '这一差异是在主要的影调与偏色问题解决之后才做的风格精修，它对最终调色的贴近程度影响很大。',
      },
    },
    aiGuideMd: `想要得到可信的匹配，就按核心差异的先后顺序来处理：**曝光定位与中间调提亮**、**黑场过重与阴影压缩**、**白平衡走向与氛围偏色**，最后是**色板分离与天空／墙面质感**。由于原图与参考图本就是不同的场景，目标应是匹配影调逻辑与色板走向，而不是凭空造出并不属于这条街景的颜色。

1. **先为「曝光定位与中间调提亮」确定整体亮度。**在「基本」面板中，从**曝光 +0.55 至 +0.80**、**高光 -15 至 -30**、**阴影 +35 至 +55**、**白色 +8 至 +18**、**黑色 +18 至 +30** 开始。目标是把画面从当前偏暗的都市影调，带入更明亮、以中间调为主导的定位。留意受光的墙面和右侧那面浅色墙。如果墙面开始丢失形体，先降低「白色」，再考虑降低「曝光」。

2. **为「黑场过重与阴影压缩」重塑对比。**曝光定好后，降低**对比度 -10 至 -20**。随后用一条平缓的点曲线来微调，而不是继续用基本面板的滑块硬压。建议的起始曲线：黑点从 **(0,0)** 抬到 **(0,10)**，暗部下方控制点约 **(40,32)** 至 **(40,42)**，中间调控制点约 **(128,128)** 至 **(128,138)**，高光上方控制点约 **(210,210)** 至 **(210,214)**。这样能抬起黑场、打开中间调，同时让亮部保持受控。如果结果开始发灰发平，用中间调控制点找回一点层次，而不是再次压低黑点。

3. **为「白平衡走向与氛围偏色」校正整体偏色。**原图需要往更暖、略偏绿的方向走。在「颜色」面板中，尝试**色温 +8 至 +14**、**色调 -8 至 -15**。这是关键一步，它让文件摆脱偏冷的品红—蓝城市观感，靠近参考图更暖的石材与空气平衡。如果陶土色墙面很快就变得过黄，先降低「色温」，把更多偏移量留给「色调」。

4. **为「色板分离与天空／墙面质感」削弱图形化的蓝橙对撞。**在动用 HSL 之前，先用克制的全局色彩控制：**自然饱和度 -8 至 -18**、**饱和度 -2 至 -8**。参考图并非没有颜色，只是它的互补色对比不那么冲、也不那么人工。要在受光建筑上保留足够的局部暖意，让画面依旧读起来像被自然光照亮。

5. **用「颜色混合器」精修色板，主要处理蓝色与橙色。**天空部分，使用**蓝色色相 -20 至 -35**、**蓝色饱和度 -20 至 -35**、**蓝色明亮度 +10 至 +22**。这会把深钴蓝的天空推向更柔和的青色，减轻厚重的数码感。受光墙面部分，使用**橙色色相 +8 至 +18**、**橙色饱和度 -10 至 -20**、**橙色明亮度 +8 至 +18**。如果陶土色仍然偏红，再加上**红色饱和度 -8 至 -15**。这些调整会把当前强烈的明信片式蓝橙对撞，转化为更柔和的石材般暖调。副作用可能是窗户和金属细节变得死板，因此在中性色开始发灰发粉之前就要停手。

6. **加入轻微的颜色分级，统一冷暖关系。**这一步要克制。可尝试**中间调 色相 42 至 50 / 饱和度 8 至 12**、**阴影 色相 52 至 60 / 饱和度 4 至 7**、**高光 色相 190 至 200 / 饱和度 4 至 8**，配合**混合 60 至 70**、**平衡 +5 至 +15**。目的不是明显的分离色调，而是让画面中建筑主体偏暖，同时让明亮的空气保持略微偏冷。如果右侧阴影墙面变脏或发绿，先回撤阴影的饱和度。

7. **用局部蒙版解决全局调色够不到的地方。**在右侧阴影墙面和主建筑下方较暗的部分，画一个画笔或线性渐变蒙版，参数大致为**曝光 +0.30 至 +0.55**、**阴影 +25 至 +40**、**黑色 +10 至 +18**、**饱和度 -5 至 -10**。这能让阴影保持可读，又不至于显得像凭空被打亮。天空部分，使用天空蒙版，配合**饱和度 -8 至 -18**、**去朦胧 -4 至 -10**，必要时再加**曝光 +0.05 至 +0.15**。目标是一片更明亮、更平静的天空，而不是戏剧化的天空。

8. **最后处理质感，让画面不那么生硬。**全局应用**纹理 -5 至 -10**、**清晰度 -5 至 -12**，如果画面仍然过于锐利，再加**去朦胧 -2 至 -6**。少量的**颗粒 数量 10 至 18**、**大小 20 至 30**、**粗糙度 40 至 55** 能让色板显得更有机。如果边缘丢失了太多建筑轮廓，先小幅找回「清晰度」，再考虑找回「对比度」。

核心检验点其实很简单：调整之后，原图应当依旧读得出是街道峡谷中被阳光照亮的建筑，但它应当在中间调更明亮、黑场更轻盈、砖石更暖，天空的蓝也不再那么咄咄逼人。一旦达到这个状态，成片就会在氛围上非常贴近参考图，而不必强行做出并不合理的转换。`,
  },
  advantagesB: {
    title: "不是另一款滤镜，也不只是一组图表",
    items: {
      styleLearning: {
        title: "先读懂参考图，再开始后期",
        description:
          "预设给你一种固定效果，PhotoScope 则帮你识别参考图的低饱和、柔和高光、冷暖关系与视觉结构。你学到的是可迁移的风格规律，而不是一套固定参数。",
      },
      gapVisualization: {
        title: "看懂数据对这张照片意味着什么",
        description:
          "专业图表能展示数据，PhotoScope 进一步把用户照片与参考图放在同一语境中比较。你不仅看到数值不同，也能看到差异落在什么区域、会影响哪种画面感受。",
      },
      aiInsights: {
        title: "用当前照片的证据，替代通用答案",
        description:
          "通用教程讲解一种方法，PhotoScope 则从这次对比中识别关键差距，说明其视觉影响，并给出有优先级的调整建议，让每一步都有画面依据。",
      },
      styleAdaptation: {
        title: "向参考图靠近，也保留照片本身",
        description:
          "机械套用容易忽略原图的光线、主体和色彩基础。PhotoScope 帮你选择适合迁移的风格要素，同时保留已经成立的部分，让结果仍然属于你的作品。",
      },
    },
  },
};
